import {Injectable} from '@angular/core';
import {FilterMetadata, SortMeta} from 'primeng/api';
import {SortButton} from '../../models/PrimeNG';
import {Location} from '@angular/common';
import {Table} from 'primeng/table';
import {ActivatedRoute, Router} from '@angular/router';
import {Checklist} from '../../models/Checklist';
import {AventiObject} from '../../models/AventiObject';
import {firstValueFrom} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TableService {

  constructor(
    private location: Location,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
  }

  async onSort(sortArray: SortMeta[],
    sortButtons: SortButton[],
    tableSort: SortMeta[],
    queryParamFilter: { [p: string]: FilterMetadata },
    tableFiltersReady: boolean
  ) {
    sortButtons.map(button => {
      button.sort.order = 0;
    });
    sortArray.map(sort => {
      sortButtons.map(button => {
        if (button.sort.field === sort.field) {
          button.sort.order = sort.order;
        }
      });
    });
    sortButtons = this.setSortButtonsIndex(sortButtons, tableSort);
    await this.setQueryParamsFromTableEvents(tableSort, tableFiltersReady, queryParamFilter);
    return sortButtons;
  }

  async sortFromButton(button: SortButton,
    sortButtons: SortButton[],
    tableSortInput: SortMeta[],
    queryParamFilter: { [p: string]: FilterMetadata },
    tableFiltersReady: boolean,
    numberOfTableSorts: number
  ) {
    const buttonSort: SortMeta = JSON.parse(JSON.stringify(button.sort));
    switch (buttonSort.order) {
    case 1:
      buttonSort.order = -1;
      break;
    case 0:
    case -1:
      buttonSort.order = 1;
      break;
    }
    let tableSort: SortMeta[] = JSON.parse(JSON.stringify(tableSortInput) ?? '[]');
    if (!tableSort || tableSort.length === 0) {
      tableSort = [buttonSort];
    } else {
      const sortExists = tableSort.find(sort => sort.field === buttonSort.field);
      if (sortExists) {
        tableSort = tableSort.map(sort => {
          if (sort.field === buttonSort.field) {
            return buttonSort;
          }
          return sort;
        });
      } else {
        tableSort.push(buttonSort);
      }
    }
    sortButtons = this.setSortButtonsIndex(sortButtons, tableSort);
    await this.setQueryParamsFromTableEvents(tableSort, tableFiltersReady, queryParamFilter);
    numberOfTableSorts = this.setNumberOfTableSorts(tableSort);
    return {tableSort, numberOfTableSorts, sortButtons};
  }


  async clearTable(table: Table, tableSort: SortMeta[], sortButtons: SortButton[]) {
    table.clear();
    table.reset();
    table.clearFilterValues();
    table.clearState();
    sortButtons = this.resetSortButtonsIndex(sortButtons);
    tableSort = [{field: '', order: 0}];
    const numberOfTableFilters = 0;
    const numberOfTableSorts = 0;
    const params = await firstValueFrom(this.activatedRoute.queryParams);
    const filteredParams = Object.keys(params)
      .filter(key => key.endsWith('_table'))
      .reduce((acc, key) => {
        acc[key] = params[key];
        return acc;
      }, {} as { [key: string]: any });
    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: filteredParams,
      queryParamsHandling: '',
    });
    return {
      tableSort,
      numberOfTableFilters,
      numberOfTableSorts,
      sortButtons
    };
  }

  async resetTableSort(tableSortInput: SortMeta[],
    sortButtonsInput: SortButton[],
    tableFiltersInput: { [p: string]: FilterMetadata },
    tableFiltersReadyInput: boolean,
    queryParamFilter: { [p: string]: FilterMetadata }
  ) {
    tableSortInput = [{field: '', order: 0}];
    this.resetSortButtonsIndex(sortButtonsInput);
    await this.setQueryParamsFromTableEvents(tableSortInput, tableFiltersReadyInput, queryParamFilter);
    const {
      tableSort,
      sortButtons,
      tableFilters,
      numberOfTableFilters,
      numberOfTableSorts,
      searchString
    } = this.setTableSortAndFilterFromQueryParams(tableSortInput, sortButtonsInput, tableFiltersInput, tableFiltersReadyInput);
    return {
      tableSort,
      sortButtons,
      tableFilters,
      numberOfTableFilters,
      numberOfTableSorts,
      searchString
    };
  }

  getChecklistStatusSeverity(status: string) {
    switch (status) {
    case 'signed':
      return 'success';
    case 'finished':
      return 'warning';
    case 'started':
      return 'info';
    case 'not_started':
      return 'secondary';
    case 'error':
      return 'danger';
    default:
      return 'none';
    }
  }

  async openChecklist(checklist: Checklist, disableRoute: boolean, type: string, checklistList?: any[], currentIndex?: number) {
    if (!disableRoute) {
      const state = checklistList && typeof currentIndex === 'number' ? { state: { checklistList, currentIndex } } : {};
      if (type === 'receiptform') {
        await this.router.navigateByUrl(
          'p/' +
          checklist.projectid +
          '/mt/' +
          checklist.id,
          state
        );
      } else {
        await this.router.navigateByUrl(
          'p/' +
          checklist.projectid +
          '/o/' +
          checklist.objectid +
          '/s/' +
          checklist.id,
          state
        );
      }
    }
  }

  async openObject(object: AventiObject, disableRoute: boolean) {
    if (!disableRoute) {
      await this.router.navigateByUrl(
        'p/' +
        object.projectid +
        '/o/' +
        object.id
      );
    }
  }

  async setQueryParamsFromTableEvents(
    tableSort: SortMeta[],
    tableFiltersReady: boolean,
    tableFilters: { [p: string]: FilterMetadata }
  ) {
    const queryParams = {};
    let numberOfTableFilters = 0;
    // tslint:disable-next-line:forin
    for (const key in tableFilters) {
      const filterProperty = tableFilters[key];
      const filterValue = filterProperty?.value;
      const isContainsOrBoolean = filterProperty.matchMode === 'contains' || typeof filterValue === 'boolean';
      const paramKey = isContainsOrBoolean ? key : key + '_multifilter';

      let urlValue: string = null;
      if (Array.isArray(filterValue)) {
        // Multifilter: encode each value individually so a comma inside a value
        // (e.g. "Elektro og Automasjon, Ensby - Midtskog") becomes %2C and does not
        // collide with the ',' used to join the array. Only the delimiter stays a literal comma.
        if (filterValue.length > 0) {
          urlValue = filterValue.map(v => encodeURIComponent(v)).join(',');
        }
      } else if (typeof filterValue === 'boolean') {
        urlValue = filterValue.toString();
      } else if (filterValue?.length > 0) {
        const isEncoded = decodeURIComponent(filterValue) !== filterValue;
        urlValue = isEncoded ? filterValue : encodeURIComponent(filterValue);
      }

      if (urlValue !== null) {
        numberOfTableFilters++;
      }
      queryParams[paramKey] = urlValue;
    }
    for (const key in tableSort) {
      if (Object.prototype.hasOwnProperty.call(tableSort, key)) {
        const property = tableSort[key];
        const field = property?.field ?? null;
        const order = property?.order;
        if (field !== null && order !== 0) {
          queryParams[field + '_sort'] = order;
        }
      }
    }
    if (tableFiltersReady) {
      await this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: queryParams,
        queryParamsHandling: 'merge'
      });
    }
    return numberOfTableFilters;
  }

  setTableSortAndFilterFromQueryParams(tableSort: SortMeta[],
    sortButtons: SortButton[],
    tableFilters: { [p: string]: FilterMetadata },
    tableFiltersReady: boolean,
    urlInput: string = this.location.path()
  ) {
    let numberOfTableFilters = 0;
    let numberOfTableSorts = 0;
    let searchString = '';
    if (!urlInput.includes('?')) {
      return null;
    } else {
      let paramsString = urlInput;
      if (urlInput.indexOf('?') > 0) {
        paramsString = urlInput.split('?')[1];
      }
      const paramsArray = paramsString.split('&').map(param => param.split('='));

      if (paramsString.length > 0 && paramsArray.length > 0) {
        Object.values(tableFilters).map((tFilter: FilterMetadata) => {
          tFilter.value = null;
        });

        for (const param of paramsArray) {
          const key = param[0];
          if (key.indexOf('_table') > 0) {
            continue;
          }
          const value = param[1];
          let filterMetadata: FilterMetadata;
          if (key.indexOf('_sort') > 0) {
            if (tableFiltersReady) {
              const sort: SortMeta = {
                field: key.replace('_sort', ''),
                order: parseInt(value, 10)
              };
              let tableSortTemp: SortMeta[] = JSON.parse(JSON.stringify(tableSort) ?? '[]');
              const sortExitsting = tableSortTemp?.find(tsort => tsort?.field === sort?.field);
              if (sortExitsting) {
                for (const tsort of tableSortTemp) {
                  if (tsort.field === sort.field) {
                    tsort.order = sort.order;
                  }
                }
              } else {
                if (!tableSortTemp) {
                  tableSortTemp = [{field: '', order: 0}];
                }
                tableSortTemp.push(sort);
              }
              tableSort = tableSortTemp;
            }
          } else {
            const urlDecodedValue = decodeURIComponent(decodeURIComponent(value));
            if (value === 'true' || value === 'false') {
              filterMetadata = {
                value: value === 'true',
                matchMode: 'equals'
              };
              tableFilters[key] = filterMetadata;
            } else if (key.indexOf('_multifilter') > 0) {
              // Split on the delimiter at the single-decode layer (where only the joining
              // commas are literal), then decode each value so commas inside a value survive.
              const valueArray = decodeURIComponent(value)
                .split(',')
                .map(v => decodeURIComponent(v));
              filterMetadata = {
                value: valueArray,
                matchMode: 'in',
                operator: 'and'
              };
              tableFilters[key.replace('_multifilter', '')] = filterMetadata;
            } else {
              filterMetadata = {
                value: urlDecodedValue,
                matchMode: 'contains',
                operator: 'and'
              };

              tableFilters[key] = filterMetadata;
            }
            if (key === 'global') {
              searchString = urlDecodedValue;
            }
          }
        }
        sortButtons = this.setSortButtonsIndex(sortButtons, tableSort);
        Object.values(tableFilters).forEach((tFilter) => {
          if (tFilter.value !== null) {
            numberOfTableFilters++;
          }
        });
        numberOfTableSorts = this.setNumberOfTableSorts(tableSort);
      }
      return {
        tableSort,
        sortButtons,
        tableFilters,
        tableFiltersReady,
        numberOfTableFilters,
        numberOfTableSorts,
        searchString
      };
    }
  }

  async getPagesOnLoad() {
    const params = await firstValueFrom(this.activatedRoute.queryParams);
    let pageIndex = 0;
    let pageSize = 25;
    let archivedVisible = false;
    if (params.pageIndex_table) {
      pageIndex = parseInt(params.pageIndex_table, 10);
      if (pageIndex < 0) {
        pageIndex = 0;
        await this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: {
            pageIndex_table: pageIndex,
          },
          queryParamsHandling: 'merge',
        });
      }
    }
    if (params.pageSize_table) {
      pageSize = parseInt(params.pageSize_table, 10);
    }
    if (params.archivedVisible) {
      archivedVisible = params.archivedVisible === 'true';
    }
    return {
      pageIndex: pageIndex,
      pageSize: pageSize,
      archivedVisible: archivedVisible,
    };
  }

  async onPageChange(pageSize: number, pageIndex: number) {
    const queryParams = {};
    if (pageSize > 0) {
      queryParams['pageSize_table'] = pageSize;
    }
    if (pageIndex >= 0) {
      queryParams['pageIndex_table'] = pageIndex;
    }
    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  private resetSortButtonsIndex(sortButtons: SortButton[]) {
    const buttonSort: SortButton[] = sortButtons;
    buttonSort.map(button => {
      button.index = 0;
      button.sort.order = 0;
    });
    return buttonSort;
  }

  private setNumberOfTableSorts(tableSort: SortMeta[]) {
    let numberOfTableSorts = 0;
    tableSort?.forEach((tSort) => {
      if (tSort.order !== 0) {
        numberOfTableSorts++;
      }
    });
    return numberOfTableSorts;
  }

  private setSortButtonsIndex(buttonSort: SortButton[], tableSort: SortMeta[]) {
    let sortButtonIndex = 0;
    buttonSort.map(button => {
      button.index = 0;
    });
    tableSort?.map((tSort) => {
      buttonSort.map(button => {
        if (button.sort.field === tSort.field) {
          sortButtonIndex++;
          button.index = sortButtonIndex;
        }
      });
    });
    return buttonSort;
  }
}
